import { Suspense, useState, type ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Link, matchPath, useLocation } from "react-router";
import { Building2, ChartColumn, Search, Sun, Table2 } from "lucide-react";
import { Notification } from "@/components/admin/notification";
import { Error } from "@/components/admin/error";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { DataImportProvider } from "../../dataImport/DataImportProvider";
import { useConfigurationLoader } from "../../root/useConfigurationLoader";
import { PullToRefresh } from "../../layout/PullToRefresh";
import { GlobalBar } from "./GlobalBar";
import { Sidebar } from "./Sidebar";

/**
 * Estructura Raftel (Bloque A): menú izquierdo + barra global en computadora;
 * barra global, menú deslizable y 5 botones abajo en celular.
 */
export const RaftelLayout = ({ children }: { children: ReactNode }) => {
  useConfigurationLoader();
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);

  const content = (
    <ErrorBoundary FallbackComponent={Error}>
      <Suspense fallback={<Skeleton className="h-12 w-12 rounded-full" />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );

  if (isMobile) {
    return (
      <DataImportProvider>
        <PullToRefresh />
        <div className="fixed inset-x-0 top-0 z-20">
          <GlobalBar onMenu={() => setMenuOpen(true)} />
        </div>
        {/* las pantallas móviles de Atomic ponen su encabezado debajo (top-13) */}
        <main id="main-content" className="pt-13">
          {content}
        </main>
        <BottomNav />
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetContent side="left" className="w-65 p-0 gap-0">
            <SheetTitle className="sr-only">Menú</SheetTitle>
            <Sidebar onNavigate={() => setMenuOpen(false)} />
          </SheetContent>
        </Sheet>
        <Notification mobileOffset={{ bottom: "76px" }} />
      </DataImportProvider>
    );
  }

  return (
    <DataImportProvider>
      <div className="grid min-h-screen grid-cols-[232px_minmax(0,1fr)]">
        <aside className="sticky top-0 h-screen border-r bg-sidebar">
          <Sidebar />
        </aside>
        <div className="flex min-w-0 flex-col">
          <div className="sticky top-0 z-20">
            <GlobalBar />
          </div>
          <main id="main-content" className="px-5 py-4">
            <EmpresasTabs />
            {content}
          </main>
        </div>
      </div>
      <Notification />
    </DataImportProvider>
  );
};

/** Empresas y Contactos comparten sección (decisión de Ian). */
export const EmpresasTabs = () => {
  const { pathname } = useLocation();
  const onContacts = !!matchPath("/contacts", pathname);
  if (!onContacts && !matchPath("/companies", pathname)) return null;
  const tab = (to: string, label: string, active: boolean) => (
    <Link
      to={to}
      className={cn(
        "border-b-2 px-3 py-2 text-sm font-semibold",
        active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground",
      )}
    >
      {label}
    </Link>
  );
  return (
    <div className="mb-3 flex gap-1 border-b">
      {tab("/companies", "Empresas", !onContacts)}
      {tab("/contacts", "Contactos", onContacts)}
    </div>
  );
};

const NAV = [
  { to: "/", match: ["/", "/mi-dia"], label: "Mi día", Icon: Sun },
  {
    to: "/directory",
    match: ["/directory/*"],
    label: "Directorio",
    Icon: Search,
  },
  {
    to: "/board",
    match: ["/board/*", "/deals/*"],
    label: "Oportun.",
    Icon: Table2,
  },
  {
    to: "/companies",
    match: ["/companies/*", "/contacts/*"],
    label: "Empresas",
    Icon: Building2,
  },
  { to: "/tablero", match: ["/tablero"], label: "Tablero", Icon: ChartColumn },
];

const BottomNav = () => {
  const { pathname } = useLocation();
  return (
    <nav
      aria-label="Navegación"
      className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t bg-card px-1 pt-1.5"
      style={{ paddingBottom: "calc(6px + env(safe-area-inset-bottom, 0px))" }}
    >
      {NAV.map(({ to, match, label, Icon }) => {
        const active = match.some((m) => matchPath(m, pathname));
        return (
          <Link
            key={to}
            to={to}
            className={cn(
              "grid justify-items-center gap-0.5 py-1 text-[11px] font-bold",
              active ? "text-primary" : "text-muted-foreground",
            )}
          >
            <Icon className="size-5" />
            {label}
          </Link>
        );
      })}
    </nav>
  );
};
